import { config } from '../../config';
import { lockNudgeDue, overrunNudgeDue, pickStripNotice } from '../../domain/chrome';
import type { StripNoticeKind } from '../../domain/chrome';
import type { MatchRow } from '../../types';

/**
 * B-8 — the notification strip's **decisions**, pure so the Node suite drives every arm.
 *
 * The strip itself is one slot at a fixed height (design spec §2.4); *which* notice fills it is
 * the engine's arbitration — `pickStripNotice` over `STRIP_PRIORITY` (`domain/chrome.ts`), reused
 * as-is per requirements §5.2 so B cannot re-rank what the design already ordered. What is B's
 * here is only the candidate list (which conditions this UI can currently surface) and the copy.
 *
 * ── Which kinds are live in B-8, and where the rest are ──────────────────────
 *
 * | Kind | Status |
 * |---|---|
 * | `overrun` | live — `overrunNudgeDue`, the engine's render-time predicate (SES-7; pre-mortem incident 2 wants it in-band on EVERY in-match surface, which the chrome's placement gives structurally) |
 * | `writerBanner` | live — the §13.7 report; the lock reports, it never refuses (baseline §4.3) |
 * | `wakeLock` | live — the ENV-8 degraded chip |
 * | `update` | live — **B-8 is the first consumer of `notifyUpdateReady` in B** (the B-0 flag); `shouldPromptUpdate` gates it, the user taps or ignores it, nothing ever forces a reload (OBS-4). **Since B-35 it is the one kind this list withholds while the finished card is on screen** — see `cardShowing` |
 * | `lockNudge` | live since **B-10** — `lockNudgeDue`, the engine's render-time predicate (discovery phase ∧ in-match ∧ minute ≥ the threshold); its action opens B-10's lock review, and **the app never auto-locks** — the notice offers, `ConfirmLock` fires only from the review's own confirm tap. Since **B-19a** the threshold is the user's `lockNudgeMin`, defaulting to `LOCK_NUDGE_MIN` |
 * | `syncError` | never in B — the backup push service is out of scope (requirements §4) |
 *
 * The priority order itself needs no new proof here: `chrome.test.ts` owns `STRIP_PRIORITY`, and
 * `update` being last is exactly the design's "SW-update notice at lowest priority".
 */

export interface StripInputsB {
  readonly match: MatchRow;
  /** Wall clock from the rAF minute clock — the input every nudge predicate is evaluated on. */
  readonly now: number;
  /**
   * B-19a — LOCK-1's *"configurable minute"*, already parsed (`ui-b/lockNudge.ts`). Not optional:
   * the takeover host reads it inside its own load gate, so there is no render at which this
   * module would have to guess, and a defaulted parameter here would be a second place deciding
   * what "default" means.
   */
  readonly lockNudgeMin: number;
  readonly writerHoldsLock: boolean;
  readonly wakeDegraded: boolean;
  readonly wakeSupported: boolean;
  /** `shouldPromptUpdate()` as subscribed state (update waiting ∧ no harness run active). */
  readonly updateReady: boolean;
  /**
   * **B-35 / ruling R-7 — is the finished coach card on screen right now?**
   *
   * Suppresses the `update` kind and **nothing else** (§21.5): every other notice this strip can
   * carry has in-match urgency — a missed halftime whistle corrupts the match minute of every
   * later observation, and a wake lock that was refused is a screen about to go dark — while the
   * service-worker banner has none, and *"presenting to the team"* is the one context where it is
   * pure noise. The card is the surface the stakeholder holds up in front of twelve girls.
   *
   * This is a **candidate-list** clause, not an arbitration one: `pickStripNotice` and
   * `STRIP_PRIORITY` are the engine's (`domain/chrome.ts`) and are untouched, so nothing here can
   * re-rank what the design already ordered. And because the whole strip is a **pick over live
   * predicates** rather than a consuming queue, a suppressed update needs no queue and no teardown
   * — leaving the card re-offers it on the very next render.
   */
  readonly cardShowing: boolean;
}

export interface StripNoticeB {
  readonly kind: StripNoticeKind;
  /** `alert` for a condition the user is losing something by ignoring; `status` otherwise. */
  readonly role: 'status' | 'alert';
  readonly text: string;
  /** Present only for the notices that carry an action (`lockNudge` opens the review; `update` is a prompt, never a force). */
  readonly actionLabel?: string;
}

/** Every condition currently true, re-evaluated per render — a pick, never a consuming queue. */
export function activeStripKinds(input: StripInputsB): StripNoticeKind[] {
  const active: StripNoticeKind[] = [];
  // The MatchRow satisfies LockNudgeInput structurally (anchors + capturePhase) — the engine's
  // predicate carries all three clauses (discovery ∧ in-match ∧ minute ≥ the threshold), so
  // "disappears on lock" needs no teardown here: the pick re-runs on live predicates. B-19a's
  // fourth argument is the user's minute; passing it is the whole of what makes §2.4's control
  // observable, and the engine still owns every clause of the decision.
  if (lockNudgeDue(input.match, input.match.state, input.now, input.lockNudgeMin)) {
    active.push('lockNudge');
  }
  if (overrunNudgeDue(input.match, input.match.state, input.now)) active.push('overrun');
  if (!input.writerHoldsLock) active.push('writerBanner');
  if (input.wakeDegraded) active.push('wakeLock');
  if (input.updateReady && !input.cardShowing) active.push('update'); // B-35 / R-7
  return active;
}

/** The one notice the slot shows, or `null` — the slot keeps its height either way (§2.4). */
export function chooseStripNotice(input: StripInputsB): StripNoticeB | null {
  const kind = pickStripNotice(activeStripKinds(input));
  if (kind === null) return null;

  switch (kind) {
    case 'lockNudge':
      // Scenario 12 — an offer, never an act: the action opens the review sheet, and the only
      // control that locks anything is that sheet's own confirm. The engine's arbitration puts
      // this first (a time-boxed MUST outranks every standing report — `STRIP_PRIORITY`).
      // The offer IS the labeled control — "the notification strip offers 'Lock My 8'". No
      // text chip: beside the whistle and the action the chip gets ~50 px at 390 pt, and the
      // browser pass's screenshot review showed any copy clipping to uselessness there. An
      // empty `text` renders no chip (the strip's contract); the button carries the meaning.
      return {
        kind,
        role: 'status',
        text: '',
        actionLabel: `Lock my ${String(config.BOARD_CAP)}`,
      };
    case 'overrun':
      // SES-7 — states what the clock says and offers nothing: the only control that answers it
      // is the guarded hold beside this slot, and it MUST NOT auto-transition.
      return {
        kind,
        role: 'status',
        text: `Past ${String(input.match.halfLengthMin)} minutes — end the half?`,
      };
    case 'writerBanner':
      // §13.7 — names what is known and nothing more. A banner, never a gate (baseline §4.3).
      return {
        kind,
        role: 'status',
        text: 'Touchline is open in another window, which is capturing this match. Close it to capture here.',
      };
    case 'wakeLock':
      return {
        kind,
        role: 'status',
        text: input.wakeSupported
          ? 'The screen may sleep — the wake lock was refused. Capture is unaffected.'
          : 'The screen may sleep — this device has no wake lock. Capture is unaffected.',
      };
    case 'update':
      // OBS-4 — an offer the user may take at a dead spell or ignore for the whole match. Its
      // rank (last) is why it can safely exist in-match at all: any live condition outranks it.
      return { kind, role: 'status', text: 'An update is ready.', actionLabel: 'Reload' };
    /* c8 ignore next 3 -- declared in STRIP_PRIORITY, but no predicate in activeStripKinds can
       produce it in B: the backup push service is out of B's scope (requirements §4). */
    case 'syncError':
      return null;
  }
}
